import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FooterProps } from '../../types';
import { useTheme } from '../../contexts/themeContext';
import ContactFormModal, { ContactFormData } from '../sections/ContactFormModal';
import ProjectCtaSection from '../sections/ProjectCtaSection';
import { navigateToLaunchSection } from '../../utils/launchNav';

const Footer: React.FC<FooterProps> = ({
  links,
  socialLinks,
  className = '',
  navigationLocked = false,
}) => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isDark } = useTheme();

  const handleContactSubmit = (data: ContactFormData) => {
    console.log('Demande de contact:', data);
    setIsContactModalOpen(false);
  };

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    if (!navigationLocked) return;
    e.preventDefault();
    navigateToLaunchSection(navigate, pathname, href);
  };

  return (
    <footer className={`relative z-0 overflow-x-hidden ${className}`}>
      {!navigationLocked ? (
        <ProjectCtaSection onContactClick={() => setIsContactModalOpen(true)} />
      ) : null}

      {/* ======= Zone liens & bas de page ======= */}
      <div className="surface-soft relative">
        <div className="container-custom">
          {/* Logo centré */}
          <div className="flex justify-center border-b border-white/10 py-8">
            {navigationLocked ? (
              <button
                type="button"
                className="cursor-pointer"
                onClick={() => navigateToLaunchSection(navigate, pathname, '/')}
                aria-label="Provéa, accueil"
              >
                <img
                  src={isDark ? '/images/logo-blanc.png' : '/images/logo-noir.png'}
                  alt="Provéa, promoteur immobilier"
                  className="h-14 w-auto sm:h-20"
                />
              </button>
            ) : (
              <a href="/">
                <img
                  src={isDark ? '/images/logo-blanc.png' : '/images/logo-noir.png'}
                  alt="Provéa, promoteur immobilier"
                  className="h-14 w-auto sm:h-20"
                />
              </a>
            )}
          </div>

          {/* Colonnes de liens */}
          <div className="py-12">
            <div
              className={`grid gap-8 ${
                navigationLocked
                  ? 'grid-cols-1 sm:grid-cols-3 lg:gap-12'
                  : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5 lg:gap-12'
              }`}
            >
              {links.map((section, index) => (
                <div key={index}>
                  <h3 className="mb-4 font-display text-base font-semibold text-white">
                    {section.title}
                  </h3>
                  <ul className="space-y-2">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex}>
                        <a
                          href={item.href}
                          className="text-sm text-white/55 transition-colors hover:text-white"
                          onClick={(e) => handleNavClick(e, item.href)}
                        >
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Bas de page */}
          <div className="border-t border-white/10 py-6">
            <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
              <div className="text-sm text-white/45">
                © {new Date().getFullYear()} Provéa. Tous droits réservés.
              </div>

              {socialLinks && socialLinks.length > 0 && (
                <div className="flex gap-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/45 transition-colors hover:text-white"
                      aria-label={social.platform}
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              )}

              <div className="flex gap-6 text-sm">
                {navigationLocked ? (
                  <button
                    type="button"
                    onClick={() => setIsContactModalOpen(true)}
                    className="text-white/45 transition-colors hover:text-white"
                  >
                    Nous contacter
                  </button>
                ) : (
                  <>
                    <a href="/mentions-legales" className="text-white/45 transition-colors hover:text-white">
                      Mentions légales
                    </a>
                    <a href="/politique-confidentialite" className="text-white/45 transition-colors hover:text-white">
                      Politique de confidentialité
                    </a>
                    <a href="/cookies" className="text-white/45 transition-colors hover:text-white">
                      Cookies
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ContactFormModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        onSubmit={handleContactSubmit}
        title="Besoin d'information ?"
        description="Remplissez ce formulaire et nous vous recontacterons rapidement."
        primaryColor="#DF822F"
      />
    </footer>
  );
};

export default Footer;
